import { TenantMembership } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/TenantMembership";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { useLazoCompanyByTenantExternalId } from "@/hooks/queries/lazo-company/useLazoCompanyQueries";
import { useLazoTeamOfCompany } from "@/hooks/queries/lazo-team/useLazoTeamQueries";
import { LazoCompany } from "@/models/LazoCompany";
import { WorkspaceAndTenantMemberships } from "@/models/WorkspaceAndTenantMemberships";
import LocalStorageWorkspacesAndTenantMemberships from "@/services/LocalStorageTenantMembership";
import WorkspaceService from "@/services/workspaces/WorkspaceService";
import { LazoTeamOfCompany } from "@/types/LazoTeamOfCompany";
import { LAZO_USER_ROLES } from "@/types/LazoUserRoles";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type TenantContextType = {
  currentTenantMembership: TenantMembership;
  setSelectedTenantMembership: (tm: TenantMembership) => void;
  triggerFetch: () => void;
  setSelectedWorkspace: (w: WorkspaceAndTenantMemberships) => void;
  workspaceState: WorkspaceState;
  currentCompany: LazoCompany;
  lazoEmployees: LazoTeamOfCompany[];
};

export const LAZO_TENANT_EXTERNAL_ID = "lazoTenant0";

const TenantContext = createContext<TenantContextType | undefined>(undefined);

type TenantProviderProps = {
  children: React.ReactNode;
};

export type WorkspaceState = {
  workspaces: WorkspaceAndTenantMemberships[];
  selectedWorkspace: WorkspaceAndTenantMemberships;
  selectedTenantMembership: TenantMembership;
  lazoMembership: TenantMembership;
  selectionMemory: {};
  isCompanyAdmin: boolean;
  isLazoAdminOrKam: boolean;
};

export function extractCurrentSlug() {
  const hostArr = window.location.host.split(".");
  if (environment.env !== "dev") {
    if (hostArr.length === 3) {
      return hostArr[0].replaceAll("-qa", "");
    }

    return "";
  }

  if (hostArr.length === 2) {
    return hostArr[0].replaceAll("-dev", "");
  }

  if (hostArr.length === 1) {
    return "";
  }
}

export const WorkspaceEvents = { updateEvent: "WorkspaceUpdateEvent" };

// These are interceptors for the client requests to the server,
// What we do is add the current selected tenant external id to the request
// to provide subscription checking previous to letting the request continue
const registeredInterceptors = [];

export function addHeaderForChart() {
  HttpClientV2.getClient().interceptors.request.use((r) => {
    r.headers.set("X-Tenant-Ext-Id", "lazoTenant0");
    return r;
  });
}

const EMPTY_WORKSPACE_STATE: WorkspaceState = {
  selectedTenantMembership: null,
  workspaces: [],
  selectedWorkspace: null,
  lazoMembership: null,
  selectionMemory: {},
  isCompanyAdmin: false,
  isLazoAdminOrKam: false,
};

export enum TenantProviderErrorType {
  ErrorFetchingEmployees,
}

export type TenantProviderError = {
  msg: string;
  type: TenantProviderErrorType;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [workspaceState, setWorkspaceState] = useState<WorkspaceState>(EMPTY_WORKSPACE_STATE);
  const [prevState, setPrevState] = useState<WorkspaceState>({} as WorkspaceState); // For comparisons

  const { data: company } = useLazoCompanyByTenantExternalId(workspaceState.selectedTenantMembership?.tenant?.externalId);
  const { data: lazoEmployees } = useLazoTeamOfCompany(company?.externalId);

  useEffect(() => {
    if (!workspaceState || !workspaceState.selectedTenantMembership) {
      return;
    }

    while (registeredInterceptors.length > 0) {
      var popped = registeredInterceptors.pop();
      HttpClientV2.getClient().interceptors.request.eject(popped);
    }

    registeredInterceptors.push(
      HttpClientV2.getClient().interceptors.request.use((r) => {
        r.headers.set("X-Tenant-Ext-Id", workspaceState.selectedTenantMembership.tenant.externalId);
        return r;
      })
    );
  }, [workspaceState]);

  function fetchWorkspaces() {
    const preSelectTenantExternalId = window.sessionStorage.getItem("tenantExternalId");
    window.sessionStorage.removeItem("tenantExternalId");

    WorkspaceService.getWorkspacesAndTenantMembershipsForUser().then((r) => {
      r.onSuccess((content) => {
        const slug = extractCurrentSlug();

        // Find the workspace using the slug. If no workspace matches, choose the first element from the array.
        const foundCurrentWorkspace = Math.max(
          content.findIndex((w) => w.workspace.slug === slug),
          0
        );
        const lazoIdx = content.findIndex((w) => w.memberships.filter((m) => m.tenant.externalId === "lazoTenant0"));
        let lazoMembership: TenantMembership = null;
        if (lazoIdx !== -1) {
          lazoMembership = content[lazoIdx].memberships.find((m) => m.tenant.externalId === "lazoTenant0")!;
        }

        // In case we come from another workspace use the provided tenant external id to
        // find the membership
        let foundPreSelectedTenantMembership = content[foundCurrentWorkspace].memberships[0];
        if (preSelectTenantExternalId) {
          foundPreSelectedTenantMembership = content[foundCurrentWorkspace].memberships.find(
            (m) => m.tenant.externalId === preSelectTenantExternalId
          );
        }

        // Find the matching membership from fresh API data instead of reusing stale prevState
        const previousTenantExtId = prevState.selectedTenantMembership?.tenant?.externalId;
        const freshSelectedMembership = previousTenantExtId
          ? content[foundCurrentWorkspace].memberships.find((m) => m.tenant.externalId === previousTenantExtId) ||
            content[foundCurrentWorkspace].memberships[0]
          : content[foundCurrentWorkspace].memberships[0];

        const state = {
          lazoMembership: lazoMembership,
          workspaces: content,
          selectedWorkspace: content[foundCurrentWorkspace],
          selectedTenantMembership: freshSelectedMembership,
          selectionMemory: {
            [content[foundCurrentWorkspace].workspace.slug]: content[foundCurrentWorkspace].memberships[0].tenant.externalId,
          },
          isCompanyAdmin: content[foundCurrentWorkspace].memberships[0].roles?.some((r) => r.name === LAZO_USER_ROLES.COMPANY_USER_ADMIN),
          isLazoAdminOrKam:
            lazoMembership == null
              ? false
              : lazoMembership.roles?.some(
                  (r) =>
                    r.name === LAZO_USER_ROLES.LAZO_PORTAL_USER ||
                    r.name === LAZO_USER_ROLES.LAZO_PORTAL_KAM ||
                    r.name === LAZO_USER_ROLES.LAZO_TEAM_ADMIN
                ),
        };

        updateState(state);
      }).onError((m) => console.log("There was an error getting the user's Workspaces. Error: " + m));
    });
  }

  function updateState(state: WorkspaceState) {
    // Check if changed workspace
    if (JSON.stringify(prevState.selectedWorkspace) !== JSON.stringify(state.selectedWorkspace)) {
      const membershipExtId = state.selectionMemory[state.selectedWorkspace.workspace.slug];

      // If we don't have memory about this space then select the first membership
      if (!membershipExtId) {
        state.selectionMemory[state.selectedWorkspace.workspace.slug] = state.selectedWorkspace.memberships[0].tenant.externalId;
        state.selectedTenantMembership = state.selectedWorkspace.memberships[0];

        // ... else just select the saved membership
      } else {
        state.selectedTenantMembership = state.selectedWorkspace.memberships.find((m) => m.tenant.externalId === membershipExtId)!;
      }

      // Check if changed membership and save to memory
    } else if (JSON.stringify(prevState.selectedTenantMembership) !== JSON.stringify(state.selectedTenantMembership)) {
      state.selectionMemory[state.selectedWorkspace.workspace.slug] = state.selectedTenantMembership.tenant.externalId;
    }
    state.isCompanyAdmin = state.selectedTenantMembership.roles.some((r) => r.name === LAZO_USER_ROLES.COMPANY_USER_ADMIN);
    state.isLazoAdminOrKam =
      state.lazoMembership == null
        ? false
        : state.lazoMembership.roles.some(
            (r) =>
              r.name === LAZO_USER_ROLES.LAZO_PORTAL_USER ||
              r.name === LAZO_USER_ROLES.LAZO_PORTAL_KAM ||
              r.name === LAZO_USER_ROLES.LAZO_TEAM_ADMIN
          );

    setPrevState(state);
    setWorkspaceState(state);

    LocalStorageWorkspacesAndTenantMemberships.putWorkspaceState(state);

    document.dispatchEvent(new CustomEvent(WorkspaceEvents.updateEvent, { detail: state }));
  }

  useMemo(() => {
    const savedState = LocalStorageWorkspacesAndTenantMemberships.getWorkspacesState();
    //we need to check this if the users has logged from another user/workspace. It corenr but might generate issues
    if (savedState) {
      setWorkspaceState(savedState);
    }
    // Always fetch fresh data from API to ensure roles and memberships are up to date
    fetchWorkspaces();
  }, []);

  const contextValue: TenantContextType = useMemo(() => {
    return {
      setSelectedTenantMembership: (m) => updateState({ ...workspaceState, selectedTenantMembership: m }),
      setSelectedWorkspace: (w) => updateState({ ...workspaceState, selectedWorkspace: w }),
      currentTenantMembership: workspaceState.selectedTenantMembership || ({} as TenantMembership),
      workspaceState: workspaceState,
      triggerFetch: fetchWorkspaces,
      currentCompany: company,
      lazoEmployees,
    };
  }, [updateState, workspaceState, workspaceState?.selectedTenantMembership, fetchWorkspaces, company]);

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

export const useTenantMemberships = (): TenantContextType => {
  const context = useContext(TenantContext);
  return context!;
};
